import { useEffect } from "react"
import DOMPurify from "dompurify"
import { PATIENT_AVATAR_NAME, UserAvatarTooltip } from "@/components/avatar-user-tooltip"
import { PotentiallyUnsafeInnerHtml } from "@/components/potentially-unsafe-inner-html"
import { UserProvider } from "@/components/user-wrapper"
import { cn } from "@/lib/utils"

interface TimelineItemProps {
  date?: string
  username?: string
  time: string
  action: string
  isFirstOne?: boolean
  className?: string
  wrapperClassName?: string
  onClick?: VoidFunction
}

const TimelineItem = (props: TimelineItemProps) => {
  const { date, username, time, action, isFirstOne, className, wrapperClassName, onClick } = props

  // When translations include <a> tags, the DOMPurify does not allow the target attribute to be set to _blank.
  // This hook is used to set the target attribute to _blank for anchor elements.
  useEffect(() => {
    DOMPurify.addHook("afterSanitizeAttributes", (node) => {
      // set all elements owning target to target=_blank
      if ("target" in node && node instanceof HTMLAnchorElement) {
        node.setAttribute("target", "_blank")
        node.setAttribute("rel", "noopener")
      }
    })

    return () => {
      DOMPurify.removeHook("afterSanitizeAttributes")
    }
  }, [])

  return (
    <div className={cn("group flex gap-4", wrapperClassName)} data-testid="history-item" onClick={onClick}>
      <p className={cn("text-on-surface w-20 shrink-0 font-medium", { "py-1": !isFirstOne })}>{isFirstOne && date}</p>
      <p
        className={cn(
          "w-6 shrink-0 self-stretch",
          username === PATIENT_AVATAR_NAME ? "bg-ochre-surface-subdued" : "bg-lilac-surface-subdued",
          className ?? "group-first:rounded-t-full group-last:rounded-b-full group-only:h-[calc(100%-4px)]",
        )}>
        {isFirstOne && username && (
          <UserProvider
            userName={username}
            render={(user) => (
              <span data-testid="history-item-author">
                <UserAvatarTooltip user={user} uniqueIdentifier={user.userName} />
              </span>
            )}
          />
        )}
      </p>
      <p className={cn("text-on-surface-subdued w-10 shrink-0 text-sm", { "py-1": !isFirstOne })}>{time}</p>
      <PotentiallyUnsafeInnerHtml
        value={action}
        className={cn("text-on-surface [&>a]:text-primary text-sm wrap-break-word hover:[&>a]:underline", {
          "py-1": !isFirstOne,
        })}
      />
    </div>
  )
}

export { TimelineItem }
